import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import Button from './Button.jsx'
import PostModal from './PostModal.jsx'
import SelectionBar from './SelectionBar.jsx'

/** How far ahead the agenda looks. Two months covers any campaign the planner writes. */
const HORIZON_DAYS = 62

function isoDate(date) {
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * The calendar as an agenda: a plan read top to bottom, not a grid to decode.
 *
 * A month grid is how software thinks about time; a list of "tomorrow at 10, this post, approve
 * it" is how a person does. Days with nothing in them simply do not appear, each entry is the
 * picture and the one thing to do about it, and the next thing that needs a decision is always
 * at the top.
 */
export default function Calendar({
  canManage,
  business,
  refreshToken,
  onChanged,
  onShowChat,
  onAskDesigner,
}) {
  const { t, i18n } = useTranslation()
  const [days, setDays] = useState([])
  const [timezoneId, setTimezoneId] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  // Which row is working. Approving a post takes a moment on a slow phone, and greying out the
  // whole agenda says less than a spinner on the one thing that was pressed.
  const [workingId, setWorkingId] = useState(null)
  // Which post is open, by id. It used to hold the post object itself, which meant the sheet was
  // a photograph of the moment it was opened: rebuilding a picture or rewriting the words changed
  // the calendar underneath and left the sheet showing what used to be there.
  const [openId, setOpenId] = useState(null)

  // Selection: null when off; a Set of post ids while picking. A mode rather than always-on
  // checkboxes — most visits approve one post, and forty checkboxes on the way to one button is
  // furniture. Approving many one thumb-press at a time is what this replaces.
  const [selected, setSelected] = useState(null)

  const load = useCallback(async () => {
    try {
      const today = new Date()
      const end = new Date(today)
      end.setDate(end.getDate() + HORIZON_DAYS)

      const result = await api.calendar(isoDate(today), isoDate(end))

      setTimezoneId(result.timezoneId)
      setDays(result.days)
      setError(null)
    } catch (failure) {
      setError(failure.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load, refreshToken])

  const selecting = selected !== null

  function toggle(postId) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(postId)) next.delete(postId)
      else next.add(postId)
      return next
    })
  }

  /** Everything on the agenda right now — what "select all" means while looking at it. */
  const allIds = days.flatMap((day) => day.posts.map((entry) => entry.post.id))

  async function bulk(action) {
    await act(async () => {
      await api.bulkPosts(action, [...selected])
      setSelected(null)
    })
  }

  async function act(action, postId = null) {
    setBusy(true)
    setWorkingId(postId)
    try {
      await action()
      await load()
      onChanged?.()
    } catch (failure) {
      setError(failure.message)
    } finally {
      setBusy(false)
      setWorkingId(null)
    }
  }

  // Found again on every render rather than remembered. A post that leaves the calendar — thrown
  // away, or rewritten into a draft with no day yet — closes the sheet by simply not being there.
  const openPost =
    days.flatMap((day) => day.posts).map((entry) => entry.post).find((post) => post.id === openId) ?? null

  /** "Today", "Tomorrow", then the weekday and date, spelled out in the owner's language. */
  function label(dateString) {
    const date = new Date(`${dateString}T00:00:00`)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)

    if (dateString === isoDate(today)) return t('calendar.today')
    if (dateString === isoDate(tomorrow)) return t('calendar.tomorrow')

    return date.toLocaleDateString(i18n.resolvedLanguage, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
  }

  const today = isoDate(new Date())
  const empty = days.every((day) => day.posts.length === 0)

  return (
    <section className="card">
      <div className="agenda__head">
        <h2>{t('calendar.title')}</h2>
        {canManage && !empty && (
          <button
            type="button"
            className="button button--quiet"
            onClick={() => setSelected(selecting ? null : new Set())}
          >
            {selecting ? t('common.cancel') : t('calendar.select')}
          </button>
        )}
      </div>
      {timezoneId && <p className="muted small">{t('calendar.timezone', { zone: timezoneId })}</p>}

      {error && <p className="status status--bad">{error}</p>}

      {empty ? (
        <div className="agenda__empty">
          <p>{t('calendar.empty')}</p>
          {canManage && onShowChat && (
            <button type="button" className="button" onClick={onShowChat}>
              {t('calendar.emptyCta')}
            </button>
          )}
        </div>
      ) : (
        <div className="agenda">
          {days.map((day) => (
            <div key={day.date}>
              <p className={`agenda__label${day.date === today ? ' agenda__label--today' : ''}`}>
                {label(day.date)}
              </p>

              {day.posts.map((entry) => {
                const variant = entry.post.variants[0]

                return (
                  <div
                    key={entry.post.id}
                    className={`agenda__card${entry.planned ? ' agenda__card--planned' : ''}${
                      selecting && selected.has(entry.post.id) ? ' agenda__card--picked' : ''
                    }`}
                    onClick={selecting ? () => toggle(entry.post.id) : undefined}
                  >
                    {selecting && (
                      <input
                        type="checkbox"
                        className="agenda__pick"
                        checked={selected.has(entry.post.id)}
                        onChange={() => toggle(entry.post.id)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={variant?.text ?? String(entry.post.id)}
                      />
                    )}

                    {variant?.imageUrl ? (
                      <img
                        className="agenda__thumb"
                        src={variant.imageUrl}
                        alt=""
                        loading="lazy"
                        onClick={selecting ? undefined : () => setOpenId(entry.post.id)}
                      />
                    ) : (
                      <div className="agenda__thumb agenda__thumb--empty">🖼</div>
                    )}

                    <div className="agenda__body" onClick={selecting ? undefined : () => setOpenId(entry.post.id)}>
                      <div className="agenda__meta">
                        <span className="agenda__time">{entry.time}</span>
                        <span className={`pill ${entry.planned ? 'pill--planned' : 'pill--scheduled'}`}>
                          {t(entry.planned ? 'calendar.planned' : 'calendar.scheduled')}
                        </span>
                      </div>
                      {entry.post.products?.[0]?.name && (
                        <div className="agenda__name">{entry.post.products[0].name}</div>
                      )}
                      <div className="agenda__title">{variant?.text}</div>
                    </div>

                    {canManage &&
                      !selecting &&
                      (entry.planned ? (
                        // A slot a campaign planned. Approving it here is what makes it real.
                        <Button
                          type="button"
                          className="button"
                          disabled={busy}
                          loading={workingId === entry.post.id}
                          onClick={() => act(() => api.approvePost(entry.post.id), entry.post.id)}
                        >
                          {t('calendar.approve')}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          className="button button--quiet"
                          disabled={busy}
                          loading={workingId === entry.post.id}
                          onClick={() => act(() => api.unschedulePost(entry.post.id), entry.post.id)}
                        >
                          {t('calendar.unschedule')}
                        </Button>
                      ))}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}

      {selecting && (
        <SelectionBar
          selected={selected}
          allIds={allIds}
          busy={busy}
          onSelect={setSelected}
          onApprove={() => bulk('approve')}
          onRemove={() => bulk('reject')}
        />
      )}

      <PostModal
        post={openPost}
        business={business}
        canManage={canManage}
        busy={busy}
        onClose={() => setOpenId(null)}
        onAskDesigner={onAskDesigner}
        timezoneId={timezoneId}
        onChanged={() => act(async () => {})}
        onReplaced={(id) => setOpenId(id)}
      />
    </section>
  )
}
